// Address & opening-hours verification — admin attests that the public
// NAP block matches reality. When verified, a user-facing trust badge is
// shown in the footer and contact page.

import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { assertAdmin } from "@/lib/admin-guard";
import { z } from "zod";

const SITE_CONFIG_KEY = "address_verification";

export interface AddressVerification {
  verified: boolean;
  /** ISO timestamp the address was last attested as correct. */
  verifiedAt: string | null;
  /** Email of the admin who attested it (for audit). */
  verifiedBy: string | null;
  /** Snapshot of the verified values — badge only renders when these match the live config. */
  streetAddress: string;
  postalCode: string;
  hoursVerified: boolean;
  hoursVerifiedAt: string | null;
  /** Optional admin notes (e.g. "checked against Companies House"). */
  notes: string;
}

export const defaultVerification: AddressVerification = {
  verified: false,
  verifiedAt: null,
  verifiedBy: null,
  streetAddress: "",
  postalCode: "",
  hoursVerified: false,
  hoursVerifiedAt: null,
  notes: "",
};

function mergeVerification(raw: unknown): AddressVerification {
  if (!raw || typeof raw !== "object") return defaultVerification;
  const r = raw as Partial<AddressVerification>;
  return {
    ...defaultVerification,
    ...r,
    verified: !!r.verified,
    hoursVerified: !!r.hoursVerified,
  };
}

async function getAdminClient() {
  const url = process.env.SUPABASE_URL;
  const key = process.env.SUPABASE_SERVICE_ROLE_KEY;
  if (!url || !key) throw new Error("Server configuration error");
  const { createClient } = await import("@supabase/supabase-js");
  return createClient(url, key, { auth: { persistSession: false, autoRefreshToken: false } });
}

export const getVerification = createServerFn({ method: "GET" })
  .handler(async (): Promise<AddressVerification> => {
    try {
      const supabase = await getAdminClient();
      const { data } = await supabase
        .from("site_config")
        .select("value")
        .eq("key", SITE_CONFIG_KEY)
        .maybeSingle();
      return mergeVerification((data as { value?: unknown } | null)?.value);
    } catch (err) {
      console.error("getVerification failed", err);
      return defaultVerification;
    }
  });

const writeSchema = z.object({
  verified: z.boolean(),
  streetAddress: z.string().max(240),
  postalCode: z.string().max(20),
  hoursVerified: z.boolean(),
  notes: z.string().max(500).default(""),
});

export const saveVerification = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((input: unknown) => writeSchema.parse(input))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const supabase = await getAdminClient();
    const now = new Date().toISOString();

    // Look up the admin's email for the audit stamp.
    const { data: adminRow } = await supabase
      .from("admin_users")
      .select("email")
      .eq("user_id", context.userId)
      .maybeSingle();
    const adminEmail = (adminRow as { email?: string | null } | null)?.email ?? null;

    // Pull prior so we only stamp timestamps when transitioning false → true.
    const { data: prior } = await supabase
      .from("site_config")
      .select("value")
      .eq("key", SITE_CONFIG_KEY)
      .maybeSingle();
    const previous = mergeVerification((prior as { value?: unknown } | null)?.value);

    const value: AddressVerification = {
      verified: data.verified,
      verifiedAt: data.verified ? (previous.verified ? previous.verifiedAt : now) : null,
      verifiedBy: data.verified ? (previous.verified ? previous.verifiedBy : adminEmail) : null,
      streetAddress: data.streetAddress,
      postalCode: data.postalCode,
      hoursVerified: data.hoursVerified,
      hoursVerifiedAt: data.hoursVerified ? (previous.hoursVerified ? previous.hoursVerifiedAt : now) : null,
      notes: data.notes ?? "",
    };

    const { error } = await supabase
      .from("site_config")
      .upsert({ key: SITE_CONFIG_KEY, value: value as never, updated_at: now });
    if (error) {
      console.error("saveVerification failed", error);
      throw new Error("Failed to save verification");
    }

    // Audit trail — capture every change to address / postcode / verification state.
    try {
      const addrChanged = previous.streetAddress !== value.streetAddress;
      const postcodeChanged = previous.postalCode !== value.postalCode;
      const verifiedChanged = previous.verified !== value.verified;
      const hoursChanged = previous.hoursVerified !== value.hoursVerified;
      if (addrChanged || postcodeChanged || verifiedChanged || hoursChanged) {
        const action = verifiedChanged && value.verified
          ? "verification.verified"
          : verifiedChanged && !value.verified
            ? "verification.revoked"
            : "verification.updated";
        const parts: string[] = [];
        if (addrChanged) parts.push(`address: "${previous.streetAddress}" → "${value.streetAddress}"`);
        if (postcodeChanged) parts.push(`postcode: "${previous.postalCode}" → "${value.postalCode}"`);
        if (verifiedChanged) parts.push(`verified: ${previous.verified} → ${value.verified}`);
        if (hoursChanged) parts.push(`hours: ${previous.hoursVerified} → ${value.hoursVerified}`);
        await supabase.from("audit_logs").insert({
          actor_id: context.userId,
          actor_email: adminEmail,
          action,
          entity_type: "site_config",
          entity_id: SITE_CONFIG_KEY,
          summary: parts.join(" · "),
          old_value: previous as never,
          new_value: value as never,
        });
      }
    } catch (auditErr) {
      console.error("verification audit log failed (non-fatal)", auditErr);
    }

    return value;
  });

const auditFilterSchema = z.object({
  from: z.string().datetime().optional().nullable(),
  to: z.string().datetime().optional().nullable(),
  action: z.enum(["all", "verification.verified", "verification.revoked", "verification.updated"]).default("all"),
  search: z.string().max(200).default(""),
  limit: z.number().int().min(1).max(500).default(200),
}).partial().default({});

export interface VerificationAuditRow {
  id: string;
  created_at: string;
  actor_email: string | null;
  action: string;
  summary: string | null;
  old_value: AddressVerification | null;
  new_value: AddressVerification | null;
}

/** Recent address/verification changes for the admin audit panel. */
export const listVerificationAuditTrail = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .validator((input: unknown) => auditFilterSchema.parse(input ?? {}))
  .handler(async ({ data, context }): Promise<VerificationAuditRow[]> => {
    await assertAdmin(context.userId);
    const supabase = await getAdminClient();
    let q = supabase
      .from("audit_logs")
      .select("id, created_at, actor_email, action, summary, old_value, new_value")
      .eq("entity_type", "site_config")
      .eq("entity_id", SITE_CONFIG_KEY)
      .order("created_at", { ascending: false })
      .limit(data.limit ?? 200);
    if (data.from) q = q.gte("created_at", data.from);
    if (data.to) q = q.lte("created_at", data.to);
    if (data.action && data.action !== "all") q = q.eq("action", data.action);
    if (data.search && data.search.trim()) {
      const s = data.search.trim().replace(/[%_]/g, "");
      // Match either the actor's email or free-text summary.
      q = q.or(`actor_email.ilike.%${s}%,summary.ilike.%${s}%`);
    }
    const { data: rows, error } = await q;
    if (error) {
      console.error("listVerificationAuditTrail failed", error);
      return [];
    }
    return (rows ?? []) as VerificationAuditRow[];
  });


